/**
 * Vacature aanmaken/bewerken: titel, discipline, status, 3–4 scorecard-criteria, beoordelaars.
 * Beoordelaars = MT (automatisch) + teamlead(s) van de discipline (hier ook in te stellen) + wie je toevoegt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { wervingApi } from './wervingApi';
import { ACCENT, STIL, kiesbareDisciplines } from './wervingLogica';

const LEEG = { titel: '', discipline: '', status: 'concept', criteria: [{ naam: '' }, { naam: '' }, { naam: '' }], beoordelaars: [], kanalen: [], omschrijving: '' };

export default function VacatureDialoog({ open, vacature, disciplines, onClose, onOpgeslagen }) {
  const nieuw = !vacature;
  const isOpen = vacature?.soort === 'open';
  const [f, setF] = useState(LEEG);
  const [mensen, setMensen] = useState([]);
  const [teamleads, setTeamleads] = useState([]);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(vacature ? {
      titel: vacature.titel, discipline: vacature.discipline || '', status: vacature.status,
      criteria: (vacature.criteria || []).map((c) => ({ ...c })), beoordelaars: vacature.beoordelaars || [],
      kanalen: vacature.kanalen || [], omschrijving: vacature.omschrijving || '',
    } : LEEG);
    wervingApi.mensen().then((d) => setMensen(d.mensen || [])).catch(() => setMensen([]));
  }, [open, vacature]);

  const disc = useMemo(() => (disciplines || []).find((d) => d.sleutel === f.discipline), [disciplines, f.discipline]);
  useEffect(() => { setTeamleads(disc?.teamleads || []); }, [disc]);

  const zet = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const naamVan = (e) => mensen.find((m) => m.email === e)?.naam || e;
  const mt = mensen.filter((m) => m.mt).map((m) => m.naam);
  const emails = mensen.map((m) => m.email);

  const zetCriterium = (i, k, v) => setF((x) => ({ ...x, criteria: x.criteria.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));

  const opslaan = async () => {
    const criteria = f.criteria.map((c) => ({ ...c, naam: String(c.naam || '').trim() })).filter((c) => c.naam);
    if (!isOpen && f.titel.trim().length < 2) { toast.error('Geef de vacature een titel'); return; }
    if (criteria.length < 3 || criteria.length > 4) { toast.error('Kies 3 of 4 criteria voor de scorecard'); return; }
    setBezig(true);
    try {
      if (disc && JSON.stringify([...teamleads].sort()) !== JSON.stringify([...(disc.teamleads || [])].sort())) {
        await wervingApi.teamleadsZet(disc.sleutel, teamleads);
      }
      const body = { discipline: f.discipline || null, criteria, beoordelaars: f.beoordelaars, kanalen: f.kanalen,
        omschrijving: f.omschrijving, ...(isOpen ? {} : { titel: f.titel.trim(), status: f.status }) };
      const uit = nieuw ? await wervingApi.vacatureNieuw(body) : await wervingApi.vacatureWijzig(vacature.id, body);
      toast.success(nieuw ? 'Vacature aangemaakt' : 'Vacature opgeslagen');
      onOpgeslagen(uit);
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const veld = { size: 'small', fullWidth: true };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>{nieuw ? 'Nieuwe vacature' : 'Vacature bewerken'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.25} sx={{ pt: 1 }}>
          <TextField {...veld} label="Titel" value={f.titel} onChange={(e) => zet('titel')(e.target.value)} disabled={isOpen} autoFocus={nieuw} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <TextField {...veld} select label="Team" value={f.discipline} onChange={(e) => zet('discipline')(e.target.value)}>
              <MenuItem value=""><em>—</em></MenuItem>
              {kiesbareDisciplines(disciplines, vacature?.discipline).map((d) => (
                <MenuItem key={d.sleutel} value={d.sleutel}>{d.label}{d.gearchiveerd ? ' (gearchiveerd)' : ''}</MenuItem>
              ))}
            </TextField>
            <TextField {...veld} select label="Status" value={f.status} onChange={(e) => zet('status')(e.target.value)} disabled={isOpen}>
              <MenuItem value="concept">Concept</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="gesloten">Gesloten</MenuItem>
            </TextField>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Scorecard (3 of 4 criteria, score 1–4)</Typography>
            <Stack spacing={1}>
              {f.criteria.map((c, i) => (
                <Box key={c.id || `n${i}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField size="small" sx={{ flex: '0 0 42%' }} placeholder={`Criterium ${i + 1}`} value={c.naam || ''}
                    onChange={(e) => zetCriterium(i, 'naam', e.target.value)} />
                  <TextField size="small" sx={{ flex: 1 }} placeholder="Toelichting (optioneel)" value={c.toelichting || ''}
                    onChange={(e) => zetCriterium(i, 'toelichting', e.target.value)} />
                  <IconButton size="small" aria-label="Criterium weghalen" disabled={f.criteria.length <= 3}
                    onClick={() => setF((x) => ({ ...x, criteria: x.criteria.filter((_, j) => j !== i) }))}><CloseIcon fontSize="small" /></IconButton>
                </Box>
              ))}
            </Stack>
            {f.criteria.length < 4 && (
              <Button size="small" onClick={() => setF((x) => ({ ...x, criteria: [...x.criteria, { naam: '' }] }))}
                sx={{ mt: 0.5, textTransform: 'none', color: ACCENT }}>+ Criterium</Button>
            )}
            {!nieuw && <Typography sx={{ fontSize: 12, color: STIL, mt: 0.5 }}>Na de eerste beoordeling liggen de criteria vast; naam en toelichting kun je nog aanpassen.</Typography>}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Beoordelaars</Typography>
            <Typography sx={{ fontSize: 12.5, color: STIL, mb: 1 }}>
              Altijd: het MT{mt.length ? ` (${mt.join(', ')})` : ''}{disc ? ` en de teamlead van ${disc.label.toLowerCase()}` : ''}. Voeg hier anderen toe.
              Iedereen die beoordeelt heeft het recht Werving (bekijken) nodig.
            </Typography>
            {disc && (
              <Autocomplete multiple size="small" options={emails} value={teamleads} getOptionLabel={naamVan}
                onChange={(_, v) => setTeamleads(v)} sx={{ mb: 1.25 }}
                renderInput={(p) => <TextField {...p} label={`Teamlead(s) ${disc.label.toLowerCase()} — geldt voor alle vacatures in deze discipline`} />} />
            )}
            <Autocomplete multiple size="small" options={emails} value={f.beoordelaars} getOptionLabel={naamVan}
              onChange={(_, v) => zet('beoordelaars')(v)}
              renderInput={(p) => <TextField {...p} label="Extra beoordelaars voor deze vacature" />} />
          </Box>

          <Autocomplete multiple freeSolo size="small" options={['Website', 'LinkedIn', 'Indeed', 'Werkzoeken.nl', 'Instagram']}
            value={f.kanalen} onChange={(_, v) => zet('kanalen')(v)}
            renderInput={(p) => <TextField {...p} label="Kanalen (waar staat de vacature)" />} />
          <TextField {...veld} multiline minRows={2} label="Omschrijving (intern)" value={f.omschrijving}
            onChange={(e) => zet('omschrijving')(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: STIL }}>Annuleren</Button>
        <Button onClick={opslaan} disabled={bezig} variant="contained" disableElevation
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
          {nieuw ? 'Aanmaken' : 'Opslaan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
